import { Link, useParams } from "react-router-dom";
import Seo, { SITE_URL } from "@/components/feature/Seo";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { formatCount } from "@/components/feature/ArticleCard";
import { articles, siteInfo } from "@/mocks/gossipData";
import ArticleToc from "./components/ArticleToc";
import RelatedArticles from "./components/RelatedArticles";

export default function GossipDetailPage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((item) => item.id === id);

  if (!article) {
    return (
      <>
        <Seo
          title="未找到该合集｜瓜田福利社"
          description="你访问的合集可能已调整或尚未发布，请返回合集列表浏览其他内容。"
          keywords="吃瓜合集,福利吃瓜"
          path="/gossip"
        />
        <SiteLayout>
          <section className="flex w-full flex-col items-center gap-5 px-4 py-24 text-center md:px-6 md:py-32">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-500">
              <i className="ri-emotion-sad-line text-3xl" aria-hidden="true" />
            </span>
            <h1 className="font-heading text-3xl text-foreground-950">没有找到这篇合集</h1>
            <p className="max-w-md text-sm text-foreground-600">
              链接可能已调整，或者内容还在整理中。回到合集列表，看看最近更新的内容吧。
            </p>
            <Link
              to="/gossip"
              className="whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-label text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              返回合集列表
            </Link>
          </section>
        </SiteLayout>
      </>
    );
  }

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: article.title,
      description: article.summary,
      image: article.cover,
      datePublished: article.date,
      author: { "@type": "Person", name: article.editor },
      publisher: {
        "@type": "Organization",
        name: siteInfo.name,
        url: SITE_URL,
      },
      keywords: article.tags.join(","),
      mainEntityOfPage: `${SITE_URL}/gossip/${article.id}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "吃瓜合集", item: `${SITE_URL}/gossip` },
        {
          "@type": "ListItem",
          position: 3,
          name: article.title,
          item: `${SITE_URL}/gossip/${article.id}`,
        },
      ],
    },
  ];

  return (
    <>
      <Seo
        title={`${article.title}｜瓜田福利社`}
        description={article.summary}
        keywords={article.tags.join(",")}
        path={`/gossip/${article.id}`}
        ogType="article"
        jsonLd={jsonLd}
      />
      <SiteLayout>
        <section className="w-full bg-background-100 px-4 py-8 md:px-6 md:py-10">
          <Breadcrumbs
            items={[
              { label: "吃瓜合集", to: "/gossip" },
              { label: article.category, to: `/gossip?category=${article.categorySlug}` },
              { label: article.title },
            ]}
          />
        </section>

        <section className="w-full px-4 py-10 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <article className="lg:col-span-8">
              <div className="flex flex-col gap-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Link
                    to={`/gossip?category=${article.categorySlug}`}
                    className="whitespace-nowrap rounded-full bg-primary-100 px-3 py-1 text-xs font-label text-primary-700 transition-colors hover:bg-primary-200 cursor-pointer"
                  >
                    {article.category}
                  </Link>
                  {article.isHot && (
                    <span className="flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-500 px-3 py-1 text-xs font-label text-background-50">
                      <i className="ri-fire-line" aria-hidden="true" />
                      热瓜
                    </span>
                  )}
                </div>

                <h1 className="font-heading text-3xl leading-snug text-foreground-950 md:text-5xl">
                  {article.title}
                </h1>

                <p className="text-base leading-relaxed text-foreground-600">{article.summary}</p>

                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-background-200 py-4 text-xs text-foreground-500 md:text-sm">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line" aria-hidden="true" />
                    {article.editor}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line" aria-hidden="true" />
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-eye-line" aria-hidden="true" />
                    {formatCount(article.readCount)} 次阅读
                  </span>
                </div>
              </div>

              <figure className="mt-8 overflow-hidden rounded-2xl border border-background-200">
                <img
                  src={article.cover}
                  alt={article.coverAlt}
                  className="h-64 w-full object-cover object-top md:h-[420px]"
                />
              </figure>

              <div className="mt-10 flex flex-col gap-10">
                {article.sections.map((section, index) => (
                  <section key={section.title} id={`section-${index}`} className="scroll-mt-28">
                    <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">
                      {section.title}
                    </h2>
                    <div className="mt-4 flex flex-col gap-4">
                      {section.body.map((paragraph) => (
                        <p
                          key={paragraph.slice(0, 18)}
                          className="text-base leading-loose text-foreground-700"
                        >
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </section>
                ))}
              </div>

              <div className="mt-10 flex flex-wrap gap-2 border-t border-background-200 pt-6">
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/gossip?q=${encodeURIComponent(tag)}`}
                    className="whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-label text-secondary-900 transition-colors hover:bg-secondary-200 cursor-pointer"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <div className="mt-8 flex flex-col gap-3 rounded-xl bg-background-100 p-5 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-foreground-600">
                  看完了？回列表继续，还有更多精选盘点在等你。
                </p>
                <Link
                  to="/gossip"
                  className="flex w-fit items-center gap-2 whitespace-nowrap rounded-full bg-foreground-950 px-5 py-2.5 text-sm font-label text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                >
                  <i className="ri-arrow-left-line" aria-hidden="true" />
                  返回合集列表
                </Link>
              </div>
            </article>

            <div className="lg:col-span-4">
              <ArticleToc sections={article.sections} currentId={article.id} />
            </div>
          </div>
        </section>

        <RelatedArticles currentId={article.id} categorySlug={article.categorySlug} />
      </SiteLayout>
    </>
  );
}